/** Normalize ISO-style codes; preserve the existing USD fallback for legacy stores. */
export const normalizeCurrency = (value) => {
  const currency = typeof value === "string" ? value.trim().toUpperCase() : "";
  return /^[A-Z]{3}$/.test(currency) ? currency : "USD";
};

/**
 * Create independent formatters bound to one store currency. Missing or malformed
 * codes use USD; the named `numberFormat` export below remains USD for billing.
 * Currency values retain compact notation and default to two decimal places.
 *
 * @param {string|null|undefined} currencyCode - Case-insensitive three-letter code.
 */
export const createNumberFormat = (currencyCode) => {
  const code = normalizeCurrency(currencyCode);
  const numberFormat = {
    currencyCode: code,
    currencySymbol:
      new Intl.NumberFormat("en-US", { style: "currency", currency: code })
        .formatToParts(0)
        .find(({ type }) => type === "currency")?.value || code,
    whole: (value, minimumFractionDigits = 0, maximumFractionDigits = 2) => {
      // round to whole number
      return new Intl.NumberFormat(
        typeof navigator === "undefined"
          ? "en-US"
          : navigator.language || "en-US",
        {
          maximumFractionDigits,
          minimumFractionDigits,
        }
      ).format(value);
    },
    currency: (value, minimumFractionDigits = 2) => {
      // Chart callbacks may pass a series name as their second argument. Only
      // explicit numeric precision overrides belong in Intl's digit options.
      const precision =
        Number.isInteger(minimumFractionDigits) &&
        minimumFractionDigits >= 0 &&
        minimumFractionDigits <= 20
          ? minimumFractionDigits
          : 2;
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: Math.max(2, precision),
        minimumFractionDigits: precision,
        style: "currency",
        currency: code,
      }).format(value);
    },
    currencyBrackets: (value) => {
      // normal current in round brackets
      return `(${numberFormat.currency(value)})`;
    },
    currencyWithSign: (value) => {
      // two decimal places and locale formatting with + or - sign
      return `${value > 0 ? "+" : ""}${numberFormat.currency(value)}`;
    },
    percent: (value) => {
      // two decimal places and percentage sign
      return `${numberFormat.normal(value)}%`;
    },
    percentCompact: (value) => {
      // no decimal places and percentage sign
      return `${numberFormat.whole(value)}%`;
    },
    percentTimes100: (value) => {
      // two decimal places and percentage sign
      return `${numberFormat.normal(value * 100)}%`;
    },
    percentWithSign: (value) => {
      // two decimal places and percentage sign with + or - sign
      return `${value > 0 ? "+" : ""}${numberFormat.normal(value)}%`;
    },
    normal: (value) => {
      // two decimal places, locale formatting of the browser
      return new Intl.NumberFormat(
        typeof navigator === "undefined"
          ? "en-US"
          : navigator.language || "en-US",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(value);
    },
  };
  return numberFormat;
};

export const numberFormat = createNumberFormat("USD");

export const dateFormat = {
  default: (value) => {
    // default date format
    return new Intl.DateTimeFormat(window.navigator.language || "en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  },
};

export const toCapitalized = (str) => {
  if (!str || typeof str !== "string") {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
};

export const camelCaseToTitleCase = (str) =>
  str
    .replace(/([A-Z][a-z]+)/g, " $1")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
